import {
	Grid,
} from "@mui/material";
import { FormCard } from "components";
import * as routes from "enums/routes";
import * as hooks from "lib/hooks";
import * as opt from "lib/opt";
import * as constants from "lib/util/constants";
import { GetContainmentTypeConfig } from "./hooks/containmentTypeConfig";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ContainmentInfoCard, ContainmentTypeCard, SectorsAndPlantsCard, PartNumbersCard, UploadContentTypeCard } from "./cards";
import { useContainmentSubmit } from "./hooks/useContainmentSubmit";
import { StepButtons } from "./StepButtons";
import { SubmitStatusScreens } from "./SubmitStatusScreens";
import { UploadStepLayout } from "./UploadStepLayout";

export function UploadReviewScreen(): React.JSX.Element {
	const {
		loading,
		f,
		setStepStatus,
		setActiveStep,
		reset,
		resetStepStatus,
	} = hooks.UseFormContext();
	const navigate = useNavigate();
	const mccApi = hooks.UseMccApi();
	const { id } = useParams();

	const {
		file,
		containmentName,
		description,
		holdComment,
		useSameAsDescription,
		sectorTypes,
		plants,
		sectors,
		containmentInitialHold,
		containmentSubsequentHold,
		partNumber,
		hasMultipleParts,
		duns,
		uploadContentType,
	} = f.getValues();

	const containmentTypeName = useMemo(() => {
		if (!sectorTypes || sectorTypes.length === 0) return null;
		return (sectorTypes[0] as any).type?.name ?? null;
	}, [sectorTypes]);

	const typeConfig = useMemo(() => {
		return containmentTypeName ? GetContainmentTypeConfig(containmentTypeName) : undefined;
	}, [containmentTypeName]);

	const fileInfo = f.getValues("file");
	const hasFile = fileInfo.IsSome();

	// --- Submission ---
	const { status, error, data, submit } = useContainmentSubmit({
		mccApi,
		formValues: {
			file,
			containmentName,
			description,
			holdComment,
			useSameAsDescription,
			sectorTypes,
			plants,
			containmentInitialHold,
			containmentSubsequentHold,
			partNumber,
			duns,
			uploadContentType,
		},
		id,
		addSerialMode: false,
		setStepStatus,
	});

	const handleBack = () => {
		f.setValue("file", opt.None());
		f.setValue("parsed", []);
		f.clearErrors("file");
		resetStepStatus();
		setActiveStep((p) => p - 1);
	};

	const onRouteChange = (path: string) => {
		reset();
		navigate(path);
	};

	const onStartOver = () => {
		resetStepStatus();
		f.setValue("file", opt.None());
		f.setValue("parsed", []);
		f.clearErrors("file");
		setActiveStep(0);
	};

	if (status !== "NotAsked") {
		return (
			<SubmitStatusScreens
				status={status}
				data={data}
				error={error}
				loadingTitle="Processing Containment"
				successTitle="Successfully Submitted Containment"
				onDashboard={() => onRouteChange(routes.Dashboard.Path)}
				onCreateNew={() => onRouteChange(routes.Upload.Path)}
				onStartOver={onStartOver}
			/>
		);
	}

	// --- Normal: split-pane review + upload ---
	return (
		<UploadStepLayout
			leftPane={
				<FormCard label="Containment Summary">
					<Grid container spacing={2} alignItems="stretch">
						<Grid item xs={12} sm={6} sx={{ display: "flex" }}>
							<ContainmentInfoCard
								readonly
								showComment
								name={containmentName}
								description={description}
								comment={
									useSameAsDescription
										? description
										: holdComment
								}
							/>
						</Grid>
						<Grid item xs={12} sm={6} sx={{ display: "flex" }}>
						<ContainmentTypeCard
							readonly
							containmentTypeLabel={containmentTypeName}
							typeConfig={typeConfig}
							initialHoldName={containmentInitialHold?.UnwrapOrNil()?.name}
							subsequentHoldName={containmentSubsequentHold?.UnwrapOrNil()?.name}
						/>
						</Grid>
						<Grid item xs={12} sm={6} sx={{ display: "flex" }}>
							<SectorsAndPlantsCard
								readonly
								sectors={sectors || []}
								plants={plants || []}
							/>
						</Grid>
						<Grid item xs={12} sm={6} sx={{ display: "flex" }}>
							{typeConfig?.hasHolds ? (
								<PartNumbersCard
									readonly
									hasMultipleParts={hasMultipleParts}
									partNumber={partNumber}
									supplierDunsName={duns?.UnwrapOrNil()?.DUNName}
								/>
							) : (
								<UploadContentTypeCard
									readonly
									uploadContentTypeLabel={uploadContentType}
								/>
							)}
						</Grid>
					</Grid>
				</FormCard>
			}
			buttons={
				<StepButtons
					backText="Back"
					nextText="Submit"
					onBack={handleBack}
					onNext={submit}
					nextDisabled={!hasFile || loading}
				/>
			}
		/>
	);
}
